'use client';

import type { ProjectNeedle } from '@/lib/types';
import { useI18n } from '@/lib/i18n';
import Modal from './Modal';
import { Card } from './ui/Card';
import { DetailRow } from './ui/DetailRow';
import { ZineButton } from './ui/ZineButton';
import { inferNeedleKindFromProject } from '@/lib/needleAvailability';

interface ProjectNeedleDetailModalProps {
  needle: ProjectNeedle;
  onClose: () => void;
}

export default function ProjectNeedleDetailModal({ needle, onClose }: ProjectNeedleDetailModalProps) {
  const { t } = useI18n();

  const kind = inferNeedleKindFromProject(needle);
  const kindLabel =
    kind === 'fixed-circular'
      ? t.needleKindFixedCircular
      : kind === 'interchangeable-tip'
      ? t.needleKindInterchangeable
      : t.needleKindStraight;

  const title = needle.size ? `${needle.size} ${t.needle.toLowerCase()}` : t.needle;

  // 2026-05-10 build 1291 — Veronika sent a screenshot of this
  // modal asking for a redesign. Body was a soft rounded
  // bg-elevated card with sans-tertiary labels and a plain
  // outlined Close. Ported to the same B1 zine recipe as the
  // YarnDetail spec block (bordered cream paper, Row component
  // with mono caps label / display caps value, last:border-b-0)
  // and the canonical outlined CLOSE secondary action button.
  return (
    <Modal open={true} onClose={onClose} title={title}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Card>
          {needle.size && <DetailRow label={t.size} value={needle.size} />}
          <DetailRow label={t.needleKindLabel} value={kindLabel} />
          {kind === 'interchangeable-tip' && needle.cableLength && (
            <DetailRow label={t.cableLength} value={needle.cableLength} />
          )}
          {kind === 'fixed-circular' && needle.cableLength && (
            <DetailRow label={t.cableLength} value={needle.cableLength} />
          )}
          {needle.needleLength && <DetailRow label={t.needleLength} value={needle.needleLength} />}
          {needle.usedFor && <DetailRow label={t.usedFor} value={needle.usedFor} />}
        </Card>

        <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
          <ZineButton variant="outlined" onClick={onClose}>
            {t.close}
          </ZineButton>
        </div>
      </div>
    </Modal>
  );
}

